import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import PhotoGallery from './PhotoGallery/PhotoGallery';
import DragDropFiles from '../../../DragAndDrop/DragDropFiles';
import {
  getSiteInfo,
  addImageToSite,
  removeImageFromSite,
  updateImage,
} from '../../../../utils/api/site';
import { ToastProvider } from '../../../../providers';
import { withContextProvider } from '../../../../hoc';
import {
  useToasts, useUnsavedChangesWarning, useCloseDialogWithUnsavedChanges,
} from '../../../../hooks';
import { CloseIcon } from '../../../Icons/Icons';
import Button from '../../../Button/Button';
import tailwindTheme from '../../../../../../../config/tailwind.config';

const SitePhotoEditModal = ({
  siteId,
  organizationId,
  isShow,
  onClose,
  updateCarouselImages,
  currentSiteImages,
  setCurrentSiteImages,
}) => {
  // Hook Toast
  const { showToast } = useToasts();
  // Flag indicating there are unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Array of image IDs staged for removal
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [isFileOverBox, setIsFileOverBox] = useState(false);
  const allowedFileTypes = ['.jpeg', '.png', '.jpg'];

  const [localSiteImages, setLocalSiteImages] = useState([]);

  const boxRef = useRef(null);

  useUnsavedChangesWarning(hasUnsavedChanges);

  const handleCloseDialog = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges: setHasUnsavedChanges,
    redirectCallback: onClose,
  });

  useEffect(() => {
    if (isShow) {
      setLocalSiteImages([...currentSiteImages]);
      setImagesToRemove([]);
      setHasUnsavedChanges(false);
    }
  }, [isShow, currentSiteImages]);

  useEffect(() => {
    if (localSiteImages && localSiteImages.length > 6) {
      boxRef.current?.classList.remove('lg:w-235');
      boxRef.current?.classList.add('lg:w-239.25');
      boxRef.current?.classList.add('lg:overflow-y-auto');
      boxRef.current?.classList.remove('lg:overflow-y-hidden');
    } else {
      boxRef.current?.classList.add('lg:w-235');
      boxRef.current?.classList.remove('lg:w-239.25');
      boxRef.current?.classList.remove('lg:overflow-y-auto');
      boxRef.current?.classList.add('lg:overflow-y-hidden');
    }
  }, [localSiteImages, isShow]);

  // This function handles adding files either from a file input or drag-and-drop.
  // It reads the files, checks their types, and updates the current site images state.
  // It also handles unsaved images by assigning them a temporary ID.
  // If the file type is not allowed, it shows an error message.
  // If all allowed files have been processed, it updates the state with the new images.
  const handleAddFiles = (event) => {
    let files = null;
    if (event.target && event.target.type === 'file') {
      files = event.target.files;
    } else if (event.dataTransfer) {
      files = event.dataTransfer.files;
    }

    if (event.dataTransfer) {
      event.preventDefault();
    }

    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      const newImages = [];

      let filesRead = 0;

      newFiles.forEach((file, index) => {
        const reader = new FileReader();

        const fileName = file.name;
        const fileExtension = `.${fileName.split('.').pop().toLowerCase()}`;

        if (allowedFileTypes.includes(fileExtension)) {
          reader.onload = (readerEvt) => {
            const base64String = readerEvt.target.result;

            const newImage = siteId
              ? {
                id: `temp-${Date.now()}-${newImages.length}`, // Use newImages.length for unique temp ID
                unsaved: true,
                image: base64String,
                position: localSiteImages.length + newImages.length,
              }
              : {
                id: localSiteImages.length + newImages.length,
                image: base64String,
                position: localSiteImages.length + newImages.length,
              };

            newImages.push(newImage);
            filesRead += 1;

            // Once all *allowed* files have been processed
            const totalAllowedFiles = newFiles.filter(f => allowedFileTypes.includes(`.${f.name.split('.').pop().toLowerCase()}`)).length;
            if (filesRead === totalAllowedFiles) {
              setLocalSiteImages((prev) => [...prev, ...newImages]);
              setHasUnsavedChanges(true);
              showToast({ message: 'Images have been added.', type: 'success' });
            }
          };
        } else {
          // Handle disallowed file
          const disallowedFileName = file.name;
          showToast({ message: `File type not allowed for ${disallowedFileName}. Allowed types: ${allowedFileTypes.join(', ')}`, type: 'error' });
          // We don't increment filesRead for disallowed files, but we still need to ensure the loop finishes
          // If this is the last file (allowed or not), and all allowed files have been processed, we might need a final check
          const totalAllowedFiles = newFiles.filter(f => allowedFileTypes.includes(`.${f.name.split('.').pop().toLowerCase()}`)).length;
          if (index === newFiles.length - 1 && filesRead === totalAllowedFiles) {
            setLocalSiteImages((prev) => [...prev, ...newImages]);
            setHasUnsavedChanges(true);
            if (newImages.length > 0) { // Only show success if some images were actually added
              showToast({ message: 'Images have been added.', type: 'success' });
            }
          }
          return; // Skip reading this file
        }

        reader.readAsDataURL(file);
      });
    }
    setIsFileOverBox(false);
  };

  // Unified removal handler.
  // In edit form: remove the image from the displayed list and
  // add its ID to imagesToRemove (if it isn’t unsaved).
  // In create form: remove the image locally (and update parent state).
  const handleRemoveImage = (imageToRemove) => {
    // Remove the image from the displayed list.
    setLocalSiteImages((prevImages) => prevImages.filter((img) => img.id !== imageToRemove.id));

    if (siteId) {
      if (!imageToRemove.unsaved && imageToRemove.id) {
        setImagesToRemove((prev) => [...prev, imageToRemove.id]);
      }
    }
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!siteId) {
      setCurrentSiteImages(localSiteImages);
      updateCarouselImages(localSiteImages);
      showToast({ message: 'Success! Photos will appear when you save the new site.', type: 'success' });
      setHasUnsavedChanges(false);
      onClose();
      return;
    }

    try {
      // 1. Process unsaved (new) images.
      const unsavedImages = localSiteImages.filter((img) => img.unsaved);
      const savedImages = await Promise.all(
        unsavedImages.map(async (unsavedImage) => {
          const siteImagePayload = {
            site_image: {},
            image: unsavedImage.image,
          };
          const response = await addImageToSite(siteImagePayload, siteId, organizationId);
          const data = await response.json();
          return data.site_image;
        }),
      );

      // Replace unsaved images with their saved versions.
      const images = localSiteImages.map((img) => {
        if (img.unsaved) {
          return savedImages.shift() || img;
        }
        return img;
      });

      // 2. Process removals: call API for each staged removal.
      if (imagesToRemove.length > 0) {
        await Promise.all(
          imagesToRemove.map(async (imageId) => {
            const response = await removeImageFromSite(siteId, imageId, organizationId);
            await response.json();
          }),
        );
      }

      // 3. Process order changes.
      // For each saved image, update its position on the server.
      const orderUpdatePromises = [];
      images.forEach((img, index) => {
        if (!img.unsaved && !imagesToRemove.includes(img.id)) {
          orderUpdatePromises.push(
            updateImage(organizationId, siteId, img.id, index).then((res) => res.json()),
          );
        }
      });

      const res = await getSiteInfo(siteId, organizationId);
      const data = await res.json();
      if (!data.site) {
        throw new Error(data.error);
      }
      setCurrentSiteImages(data.site.site_images);
      updateCarouselImages(data.site.site_images);
      setImagesToRemove([]);
      setHasUnsavedChanges(false);
      showToast({ message: 'Changes saved successfully.', type: 'success' });
    } catch (error) {
      showToast({ message: error.message, type: 'error' });
    }
    onClose();
  };

  // Handles closing the modal, including unsaved changes logic
  const handleClose = () => {
    handleCloseDialog();
  };

  if (!isShow) return null;

  return (
    <div
      className="fixed z-50 top-0 right-0 w-screen h-screen flex items-center justify-center bg-modal-background"
      onKeyDown={(e) => { if (e.key === 'Escape') handleClose(); }}
      onClick={handleClose}
      role="presentation"
      aria-label="Close modal"
    >
      <div
        className="bg-vamos-true-white rounded-md shadow-lg w-164 max-h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
        aria-label="Site Photo Edit Modal"
        ref={boxRef}
      >
        {/* Modal header */}
        <div className="flex justify-between items-center pt-5.5 pr-4 pb-5.5 pl-7 border-b border-vamos-blue-gray">
          <h2 className="text-2xl font-bold text-vamos-black">
            Edit images
          </h2>
          <button
            type="button"
            onClick={() => handleClose()}
            className="text-vamos-gray hover:text-vamos-black focus:outline-none"
          >
            <CloseIcon color={tailwindTheme.theme.extend.colors['vamos-black']} width={32} height={32} />
          </button>
        </div>
        {/* Modal body */}
        <div className="px-5.5">
          {localSiteImages.length === 0 && (
            <>
              <div className="flex items-center mt-5.5">
                <p>Upload your first photo to get started.</p>
              </div>
            </>
          )}
          {localSiteImages.length >= 1 && (
            <div className="flex justify-between items-center mt-5.5 mb-4">
              <p>You can add more images here.</p>
            </div>
          )}
          {localSiteImages.length > 0 && (
            <>
              <PhotoGallery
                currentSiteImages={localSiteImages}
                setCurrentSiteImages={(siteImages) => {
                  setLocalSiteImages(siteImages);
                  setHasUnsavedChanges(true);
                }}
                organizationId={organizationId}
                siteId={siteId}
                onRemoveImage={handleRemoveImage}
                imagesToRemove={imagesToRemove}
              />
            </>
          )}
          <div className="mt-4">
            <DragDropFiles
              handleAddFiles={handleAddFiles}
              dropBoxLabel="Choose images"
              isMultipleFilesAllowed
              allowedFileTypes={allowedFileTypes}
              handleDrop={handleAddFiles}
              setIsFileOverBox={setIsFileOverBox}
              isFileOverBox={isFileOverBox}
              dragDropIconPosition="above label"
              dragDropBoxHeight="h-31"
              subTextSize="text-14"
            />
          </div>
        </div>
        {/* Modal footer - positioned at the bottom */}
        <div className="flex justify-end items-center py-5.5 mt-8 pr-5.5 border-t border-vamos-blue-gray">
          <Button
            label="Cancel"
            size="md"
            color="secondary"
            className="mr-4"
            onClick={() => handleClose()}
          />
          <div>
            <Button
              label="Save"
              size="md"
              color="primary"
              onClick={() => handleSave()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

SitePhotoEditModal.propTypes = {
  siteId: PropTypes.number.isRequired,
  organizationId: PropTypes.number.isRequired,
  currentSiteImages: PropTypes.object.isRequired,
  isShow: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  updateCarouselImages: PropTypes.func.isRequired,
  setCurrentSiteImages: PropTypes.func.isRequired,
};

export default withContextProvider(ToastProvider)(SitePhotoEditModal);
